import { createContext, useContext, useState, type ReactNode } from 'react';
import { useUserControlsContext } from '../../../contexts/UserControlsContext';
import { effectiveIdentityKey } from '../../../hooks/useSyncEffectiveIdentity';
import { useCustomInviteForm } from './useCustomInviteForm';

type CustomInviteFormValue = ReturnType<typeof useCustomInviteForm>;

const CustomInviteFormContext = createContext<CustomInviteFormValue | null>(null);

function FormHolder({ identityKey, children }: { identityKey: string | null; children: ReactNode }) {
  const form = useCustomInviteForm(identityKey);
  return <CustomInviteFormContext.Provider value={form}>{children}</CustomInviteFormContext.Provider>;
}

/**
 * Holds the Invite tab's form state ABOVE `RailDock`, which unmounts its
 * whole body when the dock closes and swaps tab panes on tab change. Without
 * this the rep's typed email, picked campaign, edited copy and any in-flight
 * send result would be lost on a dock close or an Invite -> Plays -> Invite
 * round trip. The hook's queries stay gated by their own `enabled` flags
 * (lookup needs a looked-up email, invite-status needs a picked campaign), so
 * mounting this while the dock is closed fires nothing extra.
 *
 * Because this outlives the dock it also outlives an in-place identity switch
 * (the auth outlet and `UserControlsProvider` refetch without remounting), so
 * the form remounts when the effective identity genuinely changes — same
 * identity source and same flap rule as `lib/identity-scoped-reset`: an
 * unresolved identity (`userId` null) is "don't know yet", never a new
 * identity, so a transient gap keeps the draft. The first resolved identity
 * is recorded without a remount.
 *
 * Must sit inside `EnrollQueueProvider` (the hook reads `useEnrollQueue()`).
 */
export function CustomInviteFormProvider({ children }: { children: ReactNode }) {
  const { userId, impersonation } = useUserControlsContext();
  const resolvedKey = effectiveIdentityKey(userId, impersonation);
  const [lastKey, setLastKey] = useState<string | null>(resolvedKey);
  const [epoch, setEpoch] = useState(0);
  // Render-phase adjustment (state derived from props), not an effect: the
  // previous identity's draft must never paint for the new identity.
  if (resolvedKey !== null && resolvedKey !== lastKey) {
    setLastKey(resolvedKey);
    if (lastKey !== null) setEpoch(epoch + 1);
  }
  return (
    <FormHolder key={epoch} identityKey={lastKey}>
      {children}
    </FormHolder>
  );
}

export function useCustomInviteFormContext(): CustomInviteFormValue {
  const value = useContext(CustomInviteFormContext);
  if (value === null) {
    throw new Error('useCustomInviteFormContext must be used inside <CustomInviteFormProvider>');
  }
  return value;
}
